import { Box } from "@mui/material";
import { PaymentMethod, PaymentType, useAvailablePaymentGateways } from "../../../../create/PaymentMethods";
import { PaymentMethodPreview } from "./PaymentMethodPreview";
import { ManualPaymentInstructions } from "./ManualPaymentInstruccions";
import { OnlinePaymentButton } from "./OnlinePaymentButton";

interface ClientPaymentSectionProps {
  paymentMethods: any;
  paymentType: PaymentType;
  referenceNumber: string;
  canViewPayment: boolean;
  total: number;
  paymentAmount?: number;
  handleChangeStatus?: any;
  onPayPalCaptureSuccess?: () => void;
  selectedPayments: string[];
  invoiceUid?: string;
  invoiceType?: string;
}

export const ClientPaymentSection = ({
  paymentMethods,
  handleChangeStatus,
  onPayPalCaptureSuccess,
  paymentType,
  referenceNumber,
  canViewPayment,
  total,
  paymentAmount,
  selectedPayments,
  invoiceUid,
  invoiceType,
}: ClientPaymentSectionProps) => {
  const { gateways, isLoading, error } = useAvailablePaymentGateways();

  const selectedOnlineGateways = gateways.filter((gateway) => selectedPayments.includes(gateway.id));

  const hasBankTransfer = selectedPayments.includes("bank_transfer");

  const allSelectedMethods = selectedPayments;

  const bankTransferMethod = Array.isArray(paymentMethods) ? paymentMethods.find((pm: any) => pm?.provider === "bank_transfer") : null;
  const bankTransferInfo = bankTransferMethod?.bankTransferInfo;
  const bankDetails = bankTransferInfo
    ? {
        accountNumber: bankTransferInfo.accountNumber || undefined,
        routingNumber: bankTransferInfo.routingNumber || undefined,
        accountHolder: bankTransferInfo.accountHolderName || undefined,
        bankName: bankTransferInfo.bankName || undefined,
        swift: bankTransferInfo.swift || undefined,
        iban: bankTransferInfo.iban || undefined,
      }
    : undefined;

  if (!canViewPayment) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <h3>View Only Access</h3>
        <p>
          You have 'Can View' permissions for this invoice. Payment processing is restricted to users with 'Can Pay' authority. Please contact an
          authorized finance user to complete the payment.
        </p>
      </Box>
    );
  }

  if (isLoading) {
    return <Box sx={{ p: 3, textAlign: "center" }}>Loading payment methods...</Box>;
  }

  if (error) {
    return <Box sx={{ p: 3, textAlign: "center" }}>Error loading payment methods</Box>;
  }

  // Caso 1: Solo bank_transfer disponible
  if (allSelectedMethods.length === 1 && hasBankTransfer) {
    return (
      <Box>
        <ManualPaymentInstructions bankDetails={bankDetails} referenceNumber={referenceNumber} />
      </Box>
    );
  }

  // Caso 2: Un solo método online (PayPal o Stripe) sin banco
  if (allSelectedMethods.length === 1 && selectedOnlineGateways.length === 1 && !hasBankTransfer) {
    const singleGateway = selectedOnlineGateways[0];
    return (
      <Box>
        <OnlinePaymentButton
          handleChangeStatus={handleChangeStatus}
          onPayPalCaptureSuccess={onPayPalCaptureSuccess}
          paymentMethods={paymentMethods}
          gatewayId={singleGateway.id}
          displayName={singleGateway.displayName}
          totalAmount={total}
          paymentAmount={paymentAmount}
          invoiceUid={invoiceUid}
          invoiceType={invoiceType}
          onPaymentClick={(gatewayId) => {
            console.log("Processing payment with gateway:", gatewayId);
          }}
          buttonDisabled={false}
        />
      </Box>
    );
  }

  // Caso 3: Múltiples métodos de pago
  // Crear un array con los gateways online más un objeto para bank_transfer si existe
  const allGatewaysForDisplay = [
    ...selectedOnlineGateways,
    ...(hasBankTransfer
      ? [
          {
            id: "bank_transfer",
            name: "Bank Transfer",
            displayName: "Bank Transfer",
            description: "Direct bank transfer",
            iconColor: "#FF9800",
            iconName: "account_balance_wallet",
          },
        ]
      : []),
  ];

  return (
    <PaymentMethodPreview
      selectedGateways={allGatewaysForDisplay}
      totalAmount={total}
      paymentAmount={paymentAmount}
      showPreviewWrapper={false}
      paymentMethods={paymentMethods}
      handleChangeStatus={handleChangeStatus}
      onPayPalCaptureSuccess={onPayPalCaptureSuccess}
      invoiceUid={invoiceUid}
      referenceNumber={referenceNumber}
      bankDetails={bankDetails}
      invoiceType={invoiceType}
      onPaymentClick={(selectedGatewayId) => {
        console.log("Processing payment with gateway:", selectedGatewayId);
      }}
      buttonDisabled={false}
    />
  );
};
